import { useState } from "react";
import {
  AirVent,
  Ambulance,
  ArrowRight,
  Bath,
  Briefcase,
  Bug,
  Code2,
  Construction,
  Container,
  Droplets,
  Fence,
  Flame,
  Flower2,
  Gem,
  Hammer,
  HeartPulse,
  House,
  KeyRound,
  Layers,
  PaintRoller,
  Printer,
  Scissors,
  Server,
  SprayCan,
  Stethoscope,
  Sun,
  Syringe,
  Trees,
  Truck,
  Warehouse,
  WashingMachine,
  Waves,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { assetUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

interface CategoryItem {
  id: number;
  name: string;
}

// Names are matched loosely (case + punctuation ignored) so "Roofing / Solar"
// and "Roofing/Solar" are the same category.
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const ICONS: Record<string, LucideIcon> = {
  hvac: AirVent,
  landscape: Trees,
  electric: Zap,
  roofingsolar: Sun,
  commercialcleaning: SprayCan,
  paving: Construction,
  plumbing: Droplets,
  pestcontrol: Bug,
  remodelingrenovation: Hammer,
  nichemarkets: Gem,
  construction: Construction,
  poolservice: Waves,
  homehealthcare: HeartPulse,
  treeservice: Trees,
  medspa: Flower2,
  flooring: Layers,
  autorepair: Truck,
  printmarketing: Printer,
  septicportapotty: Container,
  towingrepair: Truck,
  fireplacechimney: Flame,
  softwaresaas: Code2,
  laundry: WashingMachine,
  garagedoor: Warehouse,
  kitchenbath: Bath,
  janitorialservices: SprayCan,
  salonsuites: Scissors,
  fencing: Fence,
  aducontract: House,
  epoxyfloor: PaintRoller,
  hydrationivservices: Syringe,
  mspit: Server,
  rentalarbitrage: KeyRound,
  medicaltransport: Ambulance,
  hairrestoration: Scissors,
  urgentcare: Stethoscope,
};

// Ranked by buyer interest (the "Buyer interest & asking price by industry"
// breakdown). The first eight are the featured tiles; everything after sits
// behind "View all" in this order, then any remaining categories by count.
const FEATURED = [
  "hvac", "landscape", "electric", "roofingsolar",
  "commercialcleaning", "paving", "plumbing", "pestcontrol",
];
const RANKED_REST = [
  "remodelingrenovation", "nichemarkets", "construction", "poolservice",
  "homehealthcare", "treeservice", "medspa", "flooring", "autorepair",
  "printmarketing", "septicportapotty", "towingrepair", "fireplacechimney",
  "softwaresaas", "laundry", "garagedoor",
];

interface CategoryTilesProps {
  categories: CategoryItem[];
  /** Active listing count per category id (as string). */
  counts: Record<string, number> | null;
  /** Cover photo per category id (borrowed from one of its listings). */
  images?: Record<string, string>;
  /** Currently applied category ids. */
  selectedIds: string[];
  /** Pick a category (or null to clear the category filter). */
  onSelect: (id: number | null) => void;
}

export function CategoryTiles({ categories, counts, images = {}, selectedIds, onSelect }: CategoryTilesProps) {
  const [showAll, setShowAll] = useState(false);
  if (categories.length === 0) return null;

  const byKey = new Map(categories.map((c) => [norm(c.name), c]));
  const countOf = (c: CategoryItem) => counts?.[String(c.id)] ?? 0;
  const pick = (keys: string[]) =>
    keys.map((k) => byKey.get(k)).filter((c): c is CategoryItem => !!c);

  const featured = pick(FEATURED);
  const ranked = pick(RANKED_REST);
  const placed = new Set([...featured, ...ranked].map((c) => c.id));
  const others = categories
    .filter((c) => !placed.has(c.id))
    .sort((a, b) => countOf(b) - countOf(a) || a.name.localeCompare(b.name));
  // Empty categories only lead to an empty grid, so the tag list skips them.
  const rest = [...ranked, ...others].filter((c) => !counts || countOf(c) > 0);

  const isSelected = (c: CategoryItem) => selectedIds.includes(String(c.id));
  const toggle = (c: CategoryItem) =>
    onSelect(isSelected(c) && selectedIds.length === 1 ? null : c.id);
  const iconFor = (c: CategoryItem) => ICONS[norm(c.name)] ?? Briefcase;

  return (
    <div className="mb-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-xl text-foreground sm:text-2xl">Explore by category</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">Industries ranked by buyer interest</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => onSelect(null)}
              className="text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              Clear category
            </button>
          )}
          {rest.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
            >
              {showAll ? "Show less" : "View all categories"}
              <ArrowRight className={cn("h-4 w-4 transition-transform", showAll && "rotate-90")} />
            </button>
          )}
        </div>
      </div>

      {/* Featured tiles — top eight industries */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
        {featured.map((c) => {
          const Icon = iconFor(c);
          const selected = isSelected(c);
          const img = images[String(c.id)];
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => toggle(c)}
              aria-pressed={selected}
              className={cn(
                "group flex flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                selected ? "border-primary ring-2 ring-primary/50" : "border-border/70 hover:border-primary/40",
              )}
            >
              {/* Industry photo (from one of its listings) with the icon on top;
                  falls back to the dark listing-card placeholder cover. */}
              <div className="relative flex h-24 items-center justify-center overflow-hidden bg-[linear-gradient(135deg,hsl(40_20%_14%),hsl(40_16%_9%))]">
                {img ? (
                  <img
                    src={assetUrl(img) ?? undefined}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <div
                    className="pointer-events-none absolute inset-0 opacity-40"
                    style={{ backgroundImage: "repeating-linear-gradient(135deg, hsl(43 30% 60% / 0.08) 0 10px, transparent 10px 20px)" }}
                  />
                )}
                <div className="pointer-events-none absolute inset-0 bg-black/45 transition-colors duration-200 group-hover:bg-black/35" />
                <Icon className="relative h-8 w-8 text-[#D4AF37] drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)] transition-transform duration-200 group-hover:scale-110" />
              </div>
              <div className="flex flex-1 flex-col items-center justify-center px-2 py-2 text-center">
                <span className="text-sm font-semibold leading-tight text-foreground">{c.name}</span>
                {counts && (
                  <span className="mt-0.5 text-[0.68rem] tabular-nums text-muted-foreground">
                    {countOf(c).toLocaleString()} listings
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Every other industry as tags, behind "View all" */}
      {showAll && rest.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 rounded-xl border border-border/60 bg-card/60 p-3">
          {rest.map((c) => {
            const Icon = iconFor(c);
            const selected = isSelected(c);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c)}
                aria-pressed={selected}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
                  selected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border/70 bg-card text-foreground hover:border-primary/50 hover:text-primary",
                )}
              >
                <Icon className={cn("h-3.5 w-3.5", selected ? "text-primary-foreground" : "text-primary")} />
                {c.name}
                {counts && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 text-[0.65rem] font-bold tabular-nums",
                      selected ? "bg-primary-foreground/15" : "bg-primary/15 text-primary",
                    )}
                  >
                    {countOf(c).toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
